/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, IComponent, IComponentProps, msdyn365Commerce, getCatalogId } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { getCategoryPathsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { observer } from 'mobx-react';
import * as React from 'react';
import { CategoryPathLookup } from '@msdyn365-commerce/retail-proxy';

export interface ICartIconData {
    cart?: ICartState;
}

export interface ICartIconComponentProps extends IComponentProps<ICartIconData> {
    className?: string;
    cartLabel: string;
    cartQtyLabel: string /* Upgraded to 10.0.16 */;
    telemetryContent?: ITelemetryContent;
}

interface ICatDetails {
    prodId: number;
    category: string;
    deliveryType?: string;
    firstCategory?: string;
}

export interface ICartIconComponent extends IComponent<ICartIconComponentProps> {}

const CartIconComponentActions = {};

/**
 *
 * CartIcon component
 * @extends {React.FC<ICartIconComponentProps>}
 */
const CartIcon: React.FC<ICartIconComponentProps> = observer((props: ICartIconComponentProps) => {
    const {
        cartLabel,
        data: { cart }
    } = props;
    const cartLink = getUrlSync('cart', props.context.actionContext);

    /* VSI Customization - Start - 22/10/2020*/
    /* '()' small brackets for cart qunatity are removed */
    const cartItem = cart ? (cart.hasInvoiceLine ? 0 : `${cart.totalItemsInCart}`) : 0; /* Upgraded to 10.0.16 */
    /* VSI Customization - End */

    const label = format(cartLabel, cartItem) || `Shopping bag, ${cartItem} items`;
    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', props.telemetryContent!, 'cart-icon', '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);

    const fetchProductsDetails = async () => {
        const cls = cart?.cart.CartLines;
        const productIds = cls?.map(cl => cl.ProductId);
        if (productIds) {
            const pids: number[] = [];
            for (const productId of productIds) {
                if (productId) {
                    pids.push(productId);
                }
            }

            // const { origin } = props.context.request.url.requestUrl;
            const { baseImageUrl } = props.context.request.apiSettings;
            const searchCriteriaInput = {
                Context: {
                    ChannelId: props.context.actionContext.requestContext.apiSettings.channelId,
                    CatalogId: getCatalogId(props.context.actionContext.requestContext)
                },
                IncludeAttributes: true,
                Ids: pids
            };

            const products = await searchByCriteriaAsync({ callerContext: props.context.actionContext }, searchCriteriaInput);

            const catPathLookUps: CategoryPathLookup[] = products.map(prod => {
                return { ProductId: prod.RecordId, ExtensionProperties: [] };
            });

            //     this.ChannelId = context.apiSettings.channelId;
            // this.CatalogId = getCatalogId(context);
            // this.categoryPathLooksups = categoryPathLooksups;
            // this.apiSettings = context.apiSettings;
            // this.locale = context.locale;
            const categoryPaths = await getCategoryPathsAsync(
                {
                    callerContext: props.context.actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(props.context.actionContext)
                },
                props.context.actionContext.requestContext.apiSettings.channelId,
                getCatalogId(props.context.actionContext.requestContext),
                catPathLookUps
            );
            const prodCategoryDetails: ICatDetails[] = [];
            const requiredDetails = products.map(product => {
                const { Name, PrimaryImageUrl } = product || {};
                const catPathObj = categoryPaths.find(catPath => catPath.ProductId === product.RecordId);
                let catPath = '';
                let lastCategory: string | undefined = '';
                let first_category: string | undefined;
                if (catPathObj && catPathObj.CategoryPath?.length) {
                    catPathObj.CategoryPath[0].Segments?.map(seg => {
                        if (!first_category) {
                            first_category = seg.CategoryName;
                        }
                        catPath += seg.CategoryName + '>';
                        lastCategory = seg.CategoryName;
                    });
                    if (catPath.length) {
                        catPath = catPath.substring(0, catPath.length - 2);
                    }
                }
                const cartDelType = cart?.cart.DeliveryMode;
                prodCategoryDetails.push({
                    prodId: product.RecordId,
                    category: lastCategory,
                    firstCategory: first_category,
                    deliveryType: cartDelType
                });
                return {
                    sku: product.RecordId,
                    name: Name,
                    imageUrl: PrimaryImageUrl ? `${baseImageUrl}${PrimaryImageUrl}` : '',
                    categoryPath: catPath
                };
            });

            props.context.request.cookies.set('products_categories_details', prodCategoryDetails);

            return requiredDetails;
        }
        return [];
    };

    const updateproductsInfoForDotDigital = async () => {
        // const cartLines = cart?.cart.CartLines;

        const ddProdInfoCookie = props.context.request.cookies.get('ddProdInfo');
        if (ddProdInfoCookie && Array.isArray(ddProdInfoCookie.value)) {
            const ddProdInfo = ddProdInfoCookie.value;
            if (Array.isArray(ddProdInfo) && cart?.cart.CartLines) {
                let cookieNeedsUpdate = false;
                for (const li of cart?.cart.CartLines) {
                    const lineItem = ddProdInfo.find(info => info['sku'] === li.ProductId);
                    if (!lineItem) {
                        cookieNeedsUpdate = true;
                    }
                }
                if (cookieNeedsUpdate) {
                    const lineItems = await fetchProductsDetails();
                    props.context.request.cookies.set('ddProdInfo', lineItems);
                }
            }
        } else {
            const lineItems = await fetchProductsDetails();
            props.context.request.cookies.set('ddProdInfo', lineItems);
        }
    };

    updateproductsInfoForDotDigital();

    return (
        <a
            title={label}
            className={Number(cartItem) > 0 ? 'ms-header__icon-cart fill' : 'ms-header__icon-cart'}
            href={cartLink}
            aria-label={label}
            {...attributes}
            onClick={onTelemetryClick(props.telemetryContent!, payLoad, 'cart-icon')}
        >
            {/* VSI Customization - Start - 22/10/2020 */}
            {/* Custom css classes added for cart qunatity shown inside bag icon */}
            <span className='ms-header__icon-cart__count'>{Number(cartItem) > 0 ? cartItem : ''}</span>
            <span className='sr-only'>Cart</span>
            {/* VSI Customization - End */}
        </a>
    );
});

export const CartIconComponent: React.FunctionComponent<ICartIconComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-ignore
    ICartIconComponent
>('CartIcon', { component: CartIcon, ...CartIconComponentActions });

export default CartIconComponent;
